import { Suspense, lazy } from 'react';
import './DriverProfile.css';

// Lazy-load Three.js only when a 3D model is needed
const Car3DViewer = lazy(() => import('./DriverProfileCar3D'));

/* ---------- Types ---------- */

interface DriverStat {
  key: string;
  value: string;
  unit?: string;
}

interface ResolvedDriver {
  id: string;
  first_name: string;
  last_name: string;
  nationality: string | null;
  profile_image_url: string | null;
  half_body_image_url: string | null;
  full_body_image_url: string | null;
  stats: DriverStat[] | null;
}

interface ResolvedCar {
  id: string;
  car_number: string;
  car_name: string | null;
  manufacturer: string | null;
  model_3d_url: string | null;
  side_image_url: string | null;
  number_image_url: string | null;
}

interface ResolvedSponsor {
  id: string;
  name: string;
  logo_light_url: string;
  logo_dark_url: string | null;
}

/* ---------- Component ---------- */

interface DriverProfileProps {
  orgName: string;
  primaryColor: string;
  config: Record<string, unknown> | null;
}

export default function DriverProfile({ config }: DriverProfileProps) {
  // Data is resolved server-side in the get_showcase_by_token RPC
  const driver = (config?._resolved_driver as ResolvedDriver) ?? null;
  const raceCar = (config?._resolved_car as ResolvedCar) ?? null;
  const sponsors = (config?._resolved_sponsors as ResolvedSponsor[]) ?? [];
  const carModelUrl = raceCar?.model_3d_url ?? (config?.carModelUrl as string) ?? null;

  if (!driver) {
    return (
      <div className="driver-profile driver-profile--empty">
        <p className="driver-profile__empty-text">No driver configured</p>
      </div>
    );
  }

  // Prefer full-body for TV display, fall back to half-body, then profile
  const driverPhoto = driver.half_body_image_url ?? driver.full_body_image_url ?? driver.profile_image_url;
  const hasPhoto = !!driverPhoto;
  const has3D = !!carModelUrl;
  const hasCarImage = !!raceCar?.side_image_url;
  const numberImage = raceCar?.number_image_url;
  const carNumber = raceCar?.car_number ?? null;
  const stats = driver.stats ?? [];

  // Build sponsor track — duplicate enough times for seamless scroll loop.
  // We need at least 8 items so the strip is wide enough for translateX(-50%) to loop.
  // Works even with 1–3 sponsors by repeating them multiple times.
  const minItems = 24;
  const repeatCount = sponsors.length > 0 ? Math.max(2, Math.ceil(minItems / sponsors.length) * 2) : 0;
  const sponsorTrackItems = Array.from({ length: repeatCount }, () => sponsors).flat();

  return (
    <div className="driver-profile">

      {/* ── 3D Car — full-screen rotating GLB model as background ── */}
      {has3D && (
        <div className="driver-profile__car-3d">
          <Suspense fallback={null}>
            <Car3DViewer modelUrl={carModelUrl!} />
          </Suspense>
        </div>
      )}
      {!has3D && hasCarImage && (
        <div className="driver-profile__car-image">
          <img src={raceCar!.side_image_url!} alt="Race car" draggable={false} />
        </div>
      )}

      {/* ── Vignette — multi-directional edge darkening ── */}
      <div className="driver-profile__vignette" />

      {/* ── Driver photo — left side, overlaps identity bar ── */}
      {hasPhoto && (
        <img
          className="driver-profile__photo"
          src={driverPhoto!}
          alt={`${driver.first_name} ${driver.last_name}`}
          draggable={false}
        />
      )}

      {/* ══ BOTTOM SECTION — stacked cards ══ */}
      <div className="driver-profile__bottom">

        {/* Identity bar — full-width pill with gradient border */}
        <div className="driver-profile__identity">
          <div className="driver-profile__identity-left">
            {numberImage ? (
              <img
                className="driver-profile__number-img"
                src={numberImage}
                alt={raceCar?.car_number || ''}
                draggable={false}
              />
            ) : carNumber ? (
              <div className="driver-profile__number-text">
                {carNumber}
              </div>
            ) : null}
          </div>

          <div className="driver-profile__name-block">
            <div className="driver-profile__firstname">{driver.first_name}</div>
            <div className="driver-profile__lastname">
              {driver.last_name.toUpperCase()}
            </div>
          </div>

          {driver.nationality && (
            <div className="driver-profile__nationality">
              <img
                src={`https://flagcdn.com/w80/${driver.nationality.toLowerCase()}.png`}
                alt={driver.nationality}
                className="driver-profile__flag"
              />
              <span>{driver.nationality.toUpperCase()}</span>
            </div>
          )}
        </div>

        {/* Season label */}
        {stats.length > 0 && (
          <>
            <div className="driver-profile__season-label">
              {new Date().getFullYear()} SEASON
            </div>

            {/* Stats card — full-width pill with gradient border */}
            <div className="driver-profile__stats-card">
              {stats.map((stat, i) => (
                <div key={i} className="driver-profile__stat-cell">
                  <div className="driver-profile__stat-label">
                    {stat.key.toUpperCase()}
                  </div>
                  <div className="driver-profile__stat-value">
                    {stat.value}
                    {stat.unit && (
                      <span className="driver-profile__stat-unit">
                        {stat.unit}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Sponsors card — scrolling logo strip with gradient border */}
        {sponsors.length > 0 && (
          <div className="driver-profile__sponsor-bar">
            <div className="driver-profile__sponsor-track">
              {sponsorTrackItems.map((s, i) => (
                <img
                  key={`${s.id}-${i}`}
                  className="driver-profile__sponsor-logo"
                  src={s.logo_dark_url ?? s.logo_light_url}
                  alt={s.name}
                  draggable={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
